import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "./Reveal";
import { useLang } from "@/lib/i18n";
import { useScrollTo } from "@/lib/useScrollTo";
import { PROJECTS, INDIA_PROJECTS } from "@/lib/projects";
import { ArrowUpRight } from "lucide-react";

const ProjectRow = ({ p, flip, viewCase }) => (
  <Reveal>
    <div
      data-testid={`portfolio-project-${p.slug}`}
      className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center py-10 md:py-14 border-t hairline"
    >
      <div className={`lg:col-span-7 ${flip ? "lg:order-2" : ""}`}>
        <div className="grid grid-cols-3 gap-4 md:gap-5">
          <Link
            to={`/portfolio/${p.slug}`}
            data-testid={`portfolio-link-${p.slug}`}
            className={`group block overflow-hidden ${p.img2 ? "col-span-3 sm:col-span-2" : "col-span-3"}`}
          >
            <img
              src={p.img1}
              alt={p.name}
              loading="lazy"
              className="w-full h-[280px] md:h-[440px] object-cover scale-100 group-hover:scale-105 transition-transform duration-[1.4s] ease-out"
            />
          </Link>
          {p.img2 && (
            <Link to={`/portfolio/${p.slug}`} className="group hidden sm:block col-span-1 overflow-hidden">
              <img
                src={p.img2}
                alt={`${p.name} detail`}
                loading="lazy"
                className="w-full h-full md:h-[440px] object-cover scale-100 group-hover:scale-105 transition-transform duration-[1.4s] ease-out"
              />
            </Link>
          )}
        </div>
      </div>

      <div className={`lg:col-span-5 ${flip ? "lg:order-1" : ""}`}>
        <span className="font-serif text-6xl md:text-7xl text-transparent" style={{ WebkitTextStroke: "1px rgba(46,31,39,0.3)" }}>
          {p.n}
        </span>
        <Link to={`/portfolio/${p.slug}`} className="group block mt-4">
          <h3 className="font-serif text-3xl md:text-5xl tracking-tight group-hover:text-gold-dark transition-colors duration-500">{p.name}</h3>
        </Link>
        <p className="mt-3 text-[11px] uppercase tracking-[0.3em] text-gold-dark">{p.location}</p>
        <div className="mt-8 border-t hairline pt-6 flex flex-col gap-4">
          <div className="flex items-baseline justify-between gap-6">
            <span className="text-[10px] uppercase tracking-[0.3em] text-plum/45">{p.styleLabel}</span>
            <span className="font-serif text-lg">{p.style}</span>
          </div>
          <div className="flex items-baseline justify-between gap-6">
            <span className="text-[10px] uppercase tracking-[0.3em] text-plum/45">{p.scopeLabel}</span>
            <span className="text-sm font-light text-plum/75 text-end">{p.scope}</span>
          </div>
        </div>
        <Link
          to={`/portfolio/${p.slug}`}
          data-testid={`portfolio-case-${p.slug}`}
          className="mt-8 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-plum hover:text-gold-dark transition-colors duration-300"
        >
          {viewCase} <ArrowUpRight size={16} strokeWidth={1.25} className="rtl:-scale-x-100" />
        </Link>
      </div>
    </div>
  </Reveal>
);

export const Portfolio = () => {
  const { copy } = useLang();
  const scrollTo = useScrollTo();
  const [collection, setCollection] = useState("dubai");

  const merge = (texts, data) =>
    texts.map((p, i) => ({
      ...p,
      ...data[i],
      styleLabel: copy.portfolio.styleLabel,
      scopeLabel: copy.portfolio.scopeLabel,
    }));

  const lists = {
    dubai: merge(copy.portfolio.projects, PROJECTS),
    india: merge(copy.portfolio.indiaProjects, INDIA_PROJECTS),
  };

  return (
    <section id="portfolio" data-testid="portfolio-section" className="py-16 md:py-24 px-6 md:px-12 max-w-[1600px] mx-auto">
      <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8 md:mb-4">
        <div>
          <p className="overline-label mb-6">{copy.portfolio.overline}</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
            {copy.portfolio.titleA}{" "}
            <span className="italic text-gold-dark">{copy.portfolio.titleItalic}</span>{" "}
            {copy.portfolio.titleB}
          </h2>
        </div>
        <p className="text-sm font-light text-plum/60 max-w-xs leading-relaxed">{copy.portfolio.sub}</p>
      </Reveal>

      <Reveal className="flex items-center gap-8 mt-10 md:mt-4">
        {["dubai", "india"].map((c) => (
          <button
            key={c}
            data-testid={`collection-tab-${c}`}
            onClick={() => setCollection(c)}
            className={`pb-3 text-[11px] uppercase tracking-[0.3em] transition-colors duration-300 ${
              collection === c ? "text-plum border-b border-gold-dark" : "text-plum/40 hover:text-plum"
            }`}
          >
            {copy.portfolio.collections[c]}
          </button>
        ))}
      </Reveal>

      <AnimatePresence mode="wait">
        <motion.div
          key={collection}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          {lists[collection].map((p, i) => (
            <ProjectRow key={p.slug} p={p} flip={i % 2 === 1} viewCase={copy.portfolio.viewCase} />
          ))}
        </motion.div>
      </AnimatePresence>

      <Reveal className="border-t hairline pt-12 flex justify-center">
        <button
          data-testid="portfolio-cta-btn"
          onClick={() => scrollTo("#quiz")}
          className="bg-plum text-cream text-[11px] uppercase tracking-[0.25em] px-10 py-4 hover:bg-gold hover:text-cream transition-colors duration-300"
        >
          {copy.portfolio.cta}
        </button>
      </Reveal>
    </section>
  );
};
